import { AnalyticsAlertType } from '@/types';
import { BRONCO_SUPPLIER_UUID } from '@/mocks/uuids';
import { getDemoAlertsForBroncoRestaurant } from '@/mocks/dsr-analytics/data';
import {
  RawSalesAlert,
  SalesAlert,
} from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/types';
import { useGeneratedSalesPortalAccountDetailSalesAlertsQuery } from '@/experiences/sales-portal/domain/accounts/screens/account-detail/blocs/documents/sales-portal-account-detail-sales-alerts.generated';
import { useMemo } from 'react';

interface AccountDetailSalesAlertsLoaderParams {
  restaurantUUID: string;
  supplierUUID: string;
}

const transformRawAlert = (raw: RawSalesAlert): SalesAlert | null => {
  const common = {
    alertStatus: raw.alert_status,
    chatUUID: raw.chat_uuid,
    restaurantUUID: raw.restaurant_uuid,
    subtitle: raw.subtitle,
    supplierUUID: raw.supplier_uuid,
    title: raw.title,
  };

  switch (raw.alert_type) {
    case AnalyticsAlertType.ChurnRisk:
      return {
        ...common,
        alertType: raw.alert_type,
        alertUUID: raw.alert_uuid,
      };
    case AnalyticsAlertType.LostItem:
      return {
        ...common,
        alertType: raw.alert_type,
        alertUUID: raw.alert_uuid,
        displayName: raw.display_name,
        externalItemId: raw.external_item_id,
        packUnit: raw.pack_unit,
        photoUrlList: raw.photo_url_list ?? undefined,
        variantPackUUID: raw.variant_pack_uuid,
      };
    case AnalyticsAlertType.OverduePayment:
      return {
        ...common,
        alertType: raw.alert_type,
        data: raw.data.map((detail) => ({
          alertUUID: detail.alert_uuid,
          amount: detail.amount,
          dueAt: detail.due_at,
          invoiceNumber: detail.invoice_number,
          orderInvoiceUUID: detail.order_invoice_uuid,
        })),
      };
    default:
      return null;
  }
};

const parseSalesAlerts = (alertsJson: string[]): SalesAlert[] => {
  return alertsJson.reduce<SalesAlert[]>((acc, json) => {
    try {
      const alert = transformRawAlert(JSON.parse(json));
      if (alert) {
        acc.push(alert);
      }
    } catch {
      // Skip malformed alert entry
    }
    return acc;
  }, []);
};

/**
 * Loader hook for fetching sales alerts for the Account Detail screen.
 *
 * When the supplier is Bronco and the restaurant is a Bronco demo account,
 * returns hardcoded mock alerts instead of querying the backend.
 *
 * @param params.restaurantUUID - The UUID of the restaurant
 * @param params.supplierUUID - The UUID of the supplier
 * @returns The sales alerts, loading state, and any error
 */
export const useAccountDetailSalesAlertsLoader = ({
  restaurantUUID,
  supplierUUID,
}: AccountDetailSalesAlertsLoaderParams) => {
  const demoAlerts = useMemo(() => {
    if (supplierUUID === BRONCO_SUPPLIER_UUID) {
      const rawDemoAlerts = getDemoAlertsForBroncoRestaurant(restaurantUUID);
      if (!rawDemoAlerts) {
        return undefined;
      }
      return rawDemoAlerts.reduce<SalesAlert[]>((acc, alert) => {
        const transformed = transformRawAlert(alert);
        if (transformed) {
          acc.push(transformed);
        }
        return acc;
      }, []);
    }
  }, [restaurantUUID, supplierUUID]);

  const hasDemoData = demoAlerts !== undefined;

  const { data, loading, error } =
    useGeneratedSalesPortalAccountDetailSalesAlertsQuery({
      fetchPolicy: 'cache-first',
      skip: hasDemoData,
      variables: {
        restaurantUUID,
        supplierUUID,
      },
    });

  const salesAlerts = useMemo(() => {
    if (hasDemoData) {
      return demoAlerts;
    }

    const alertsJson = data?.analyticsAlerts?.alerts_json;

    if (!alertsJson?.length) {
      return [];
    }

    return parseSalesAlerts(alertsJson);
  }, [data, hasDemoData, demoAlerts]);

  return {
    error: hasDemoData ? undefined : error,
    loading: hasDemoData ? false : loading,
    salesAlerts,
  };
};
